/**
 * Ad Analytics Components - Reusable components for ad performance display
 */

import React from 'react';
import { 
  Eye, 
  MousePointer, 
  TrendingUp, 
  DollarSign,
  BarChart3,
  Target,
  Users,
  Layout
} from 'lucide-react';

// =============================================
// METRIC CARD - Single metric display
// =============================================

export function MetricCard({ 
  title, 
  value, 
  change, 
  changeType = 'neutral',
  icon: Icon = BarChart3,
  format = 'number',
  className = ''
}) {
  const formatValue = (val) => {
    if (format === 'currency') return `$${val.toLocaleString()}`;
    if (format === 'percent') return `${val}%`;
    if (format === 'cpm') return `$${val.toFixed(2)}`;
    return val.toLocaleString();
  };

  const changeColors = {
    positive: 'text-emerald-400',
    negative: 'text-red-400',
    neutral: 'text-white/40'
  };

  return (
    <div className={`bg-white/[0.02] backdrop-blur-xl rounded-xl border border-white/[0.06] p-4 ${className}`}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-white/50 text-sm">{title}</span>
        <div className="p-2 rounded-lg bg-white/[0.04]">
          <Icon className="w-4 h-4 text-white/40" />
        </div>
      </div>
      <div className="flex items-end justify-between">
        <span className="text-2xl font-semibold text-white">
          {formatValue(value)}
        </span>
        {change !== undefined && (
          <span className={`text-xs ${changeColors[changeType]}`}>
            {changeType === 'positive' && '+'}
            {change}%
          </span>
        )}
      </div>
    </div>
  );
}

// =============================================
// ANALYTICS SUMMARY CARDS
// =============================================

export function AdAnalyticsSummary({ data, className = '' }) {
  if (!data) return null;

  return (
    <div className={`grid grid-cols-2 md:grid-cols-4 gap-4 ${className}`}>
      <MetricCard
        title="Total Impressions"
        value={data.total_impressions || 0}
        icon={Eye}
      />
      <MetricCard
        title="Total Clicks"
        value={data.total_clicks || 0}
        icon={MousePointer}
      />
      <MetricCard
        title="Click Rate (CTR)"
        value={data.ctr || 0}
        format="percent"
        icon={TrendingUp}
      />
      <MetricCard
        title="Total Spend"
        value={data.total_spend || 0}
        format="currency"
        icon={DollarSign}
      />
    </div>
  );
}

// =============================================
// SPONSOR PERFORMANCE CARD
// =============================================

export function SponsorPerformanceCard({ sponsor, performance, onClick, className = '' }) {
  return (
    <div 
      className={`bg-white/[0.02] backdrop-blur-xl rounded-xl border border-white/[0.06] p-5 cursor-pointer hover:bg-white/[0.04] transition-colors ${className}`}
      onClick={onClick}
    >
      <div className="flex items-center gap-4 mb-4">
        {sponsor.logo_url ? (
          <img src={sponsor.logo_url} alt={sponsor.name} className="w-12 h-12 rounded-lg object-cover" />
        ) : (
          <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-purple-500/20 to-blue-500/20 flex items-center justify-center">
            <Users className="w-6 h-6 text-white/40" />
          </div>
        )}
        <div>
          <h3 className="font-medium text-white">{sponsor.name}</h3>
          <span className="text-xs text-white/40 capitalize">{sponsor.tier || 'Standard'} Tier</span>
        </div>
      </div>
      
      <div className="grid grid-cols-3 gap-3">
        <div className="text-center">
          <p className="text-lg font-semibold text-white">{performance?.total_impressions?.toLocaleString() || 0}</p>
          <p className="text-xs text-white/40">Impressions</p>
        </div>
        <div className="text-center">
          <p className="text-lg font-semibold text-white">{performance?.total_clicks?.toLocaleString() || 0}</p>
          <p className="text-xs text-white/40">Clicks</p>
        </div>
        <div className="text-center">
          <p className="text-lg font-semibold text-emerald-400">{performance?.ctr || 0}%</p>
          <p className="text-xs text-white/40">CTR</p>
        </div>
      </div>
    </div>
  );
}

// =============================================
// PLACEMENT PERFORMANCE CARD
// =============================================

export function PlacementPerformanceCard({ placement, performance, className = '' }) {
  return (
    <div className={`bg-white/[0.02] backdrop-blur-xl rounded-xl border border-white/[0.06] p-4 ${className}`}>
      <div className="flex items-center gap-3 mb-3">
        <div className="p-2 rounded-lg bg-purple-500/10">
          <Layout className="w-4 h-4 text-purple-400" />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-medium text-white text-sm truncate">{placement.placement_name || placement.placement_key}</h4>
          <p className="text-xs text-white/40">{placement.page} · {placement.position}</p>
        </div>
        {placement.is_premium && (
          <span className="px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-400 text-xs">Premium</span>
        )}
      </div>
      
      <div className="grid grid-cols-4 gap-2 text-center">
        <div>
          <p className="text-sm font-medium text-white">{performance?.total_impressions?.toLocaleString() || 0}</p>
          <p className="text-xs text-white/40">Impr.</p>
        </div>
        <div>
          <p className="text-sm font-medium text-white">{performance?.total_clicks?.toLocaleString() || 0}</p>
          <p className="text-xs text-white/40">Clicks</p>
        </div>
        <div>
          <p className="text-sm font-medium text-emerald-400">{performance?.ctr || 0}%</p>
          <p className="text-xs text-white/40">CTR</p>
        </div>
        <div>
          <p className="text-sm font-medium text-white">${performance?.total_revenue?.toFixed(2) || '0.00'}</p>
          <p className="text-xs text-white/40">Revenue</p>
        </div>
      </div>
    </div>
  );
}

// =============================================
// CAMPAIGN PERFORMANCE SECTION
// =============================================

export function CampaignPerformanceSection({ campaigns, onSelectCampaign, className = '' }) {
  if (!campaigns || campaigns.length === 0) {
    return (
      <div className={`bg-white/[0.02] backdrop-blur-xl rounded-xl border border-white/[0.06] p-8 text-center ${className}`}>
        <Target className="w-10 h-10 text-white/20 mx-auto mb-3" />
        <p className="text-white/40">No campaigns yet</p>
      </div>
    );
  }

  return (
    <div className={`space-y-3 ${className}`}>
      {campaigns.map(campaign => (
        <div 
          key={campaign.id}
          className="bg-white/[0.02] backdrop-blur-xl rounded-xl border border-white/[0.06] p-4 cursor-pointer hover:bg-white/[0.04] transition-colors"
          onClick={() => onSelectCampaign?.(campaign)}
        >
          <div className="flex items-center justify-between mb-3">
            <div>
              <h4 className="font-medium text-white">{campaign.name}</h4>
              <p className="text-xs text-white/40">
                {campaign.start_date} - {campaign.end_date || 'Ongoing'}
              </p>
            </div>
            <span className={`px-2 py-1 rounded-full text-xs ${
              campaign.status === 'active' ? 'bg-emerald-500/10 text-emerald-400' :
              campaign.status === 'paused' ? 'bg-amber-500/10 text-amber-400' :
              'bg-white/[0.06] text-white/40'
            }`}>
              {campaign.status}
            </span>
          </div>
          
          <div className="grid grid-cols-4 gap-2 text-center">
            <div>
              <p className="text-sm font-medium text-white">{campaign.impressions?.toLocaleString() || 0}</p>
              <p className="text-xs text-white/40">Impressions</p>
            </div>
            <div>
              <p className="text-sm font-medium text-white">{campaign.clicks?.toLocaleString() || 0}</p>
              <p className="text-xs text-white/40">Clicks</p>
            </div>
            <div>
              <p className="text-sm font-medium text-emerald-400">{campaign.ctr || 0}%</p>
              <p className="text-xs text-white/40">CTR</p>
            </div>
            <div>
              <p className="text-sm font-medium text-white">${campaign.spend?.toFixed(2) || '0.00'}</p>
              <p className="text-xs text-white/40">Spend</p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// =============================================
// ORGANIZATION MONETIZATION METRICS
// =============================================

export function OrganizationMonetizationMetrics({ data, className = '' }) {
  if (!data) return null;

  return (
    <div className={`bg-white/[0.02] backdrop-blur-xl rounded-xl border border-white/[0.06] p-5 ${className}`}>
      <h3 className="text-lg font-medium text-white mb-4">Ad Revenue</h3>
      
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="p-4 rounded-lg bg-white/[0.02]">
          <p className="text-2xl font-semibold text-emerald-400">${data.total_revenue?.toFixed(2) || '0.00'}</p>
          <p className="text-xs text-white/40 mt-1">Total Revenue (70% share)</p>
        </div>
        <div className="p-4 rounded-lg bg-white/[0.02]">
          <p className="text-2xl font-semibold text-white">{data.total_impressions?.toLocaleString() || 0}</p>
          <p className="text-xs text-white/40 mt-1">Ad Impressions Served</p>
        </div>
      </div>
      
      <div className="grid grid-cols-3 gap-3 text-center">
        <div>
          <p className="text-lg font-medium text-white">{data.total_clicks?.toLocaleString() || 0}</p>
          <p className="text-xs text-white/40">Clicks</p>
        </div>
        <div>
          <p className="text-lg font-medium text-white">{data.ctr || 0}%</p>
          <p className="text-xs text-white/40">CTR</p>
        </div>
        <div>
          <p className="text-lg font-medium text-white">${data.total_spend?.toFixed(2) || '0.00'}</p>
          <p className="text-xs text-white/40">Ad Spend</p>
        </div>
      </div>
    </div>
  );
}

// =============================================
// PLATFORM MONETIZATION METRICS
// =============================================

export function PlatformMonetizationMetrics({ data, className = '' }) {
  if (!data) return null;

  const platformRevenue = (data.total_spend || 0) * 0.3; // 30% platform fee

  return (
    <div className={`bg-white/[0.02] backdrop-blur-xl rounded-xl border border-white/[0.06] p-5 ${className}`}>
      <h3 className="text-lg font-medium text-white mb-4">Platform Monetization</h3>
      
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        <div className="p-4 rounded-lg bg-white/[0.02]">
          <p className="text-2xl font-semibold text-emerald-400">${platformRevenue.toFixed(2)}</p>
          <p className="text-xs text-white/40 mt-1">Platform Revenue</p>
        </div>
        <div className="p-4 rounded-lg bg-white/[0.02]">
          <p className="text-2xl font-semibold text-white">${data.total_spend?.toFixed(2) || '0.00'}</p>
          <p className="text-xs text-white/40 mt-1">Total Ad Spend</p>
        </div>
        <div className="p-4 rounded-lg bg-white/[0.02]">
          <p className="text-2xl font-semibold text-white">{data.active_advertisements || 0}</p>
          <p className="text-xs text-white/40 mt-1">Active Ads</p>
        </div>
        <div className="p-4 rounded-lg bg-white/[0.02]">
          <p className="text-2xl font-semibold text-white">${data.avg_cpm?.toFixed(2) || '0.00'}</p>
          <p className="text-xs text-white/40 mt-1">Avg CPM</p>
        </div>
      </div>
      
      <div className="grid grid-cols-4 gap-3 text-center pt-4 border-t border-white/[0.06]">
        <div>
          <p className="text-lg font-medium text-white">{data.total_impressions?.toLocaleString() || 0}</p>
          <p className="text-xs text-white/40">Impressions</p>
        </div>
        <div>
          <p className="text-lg font-medium text-white">{data.total_clicks?.toLocaleString() || 0}</p>
          <p className="text-xs text-white/40">Clicks</p>
        </div>
        <div>
          <p className="text-lg font-medium text-emerald-400">{data.ctr || 0}%</p>
          <p className="text-xs text-white/40">CTR</p>
        </div>
        <div>
          <p className="text-lg font-medium text-white">${data.avg_cpc?.toFixed(2) || '0.00'}</p>
          <p className="text-xs text-white/40">Avg CPC</p>
        </div>
      </div>
    </div>
  );
}

export default {
  MetricCard,
  AdAnalyticsSummary,
  SponsorPerformanceCard,
  PlacementPerformanceCard,
  CampaignPerformanceSection,
  OrganizationMonetizationMetrics,
  PlatformMonetizationMetrics
};
